/**
 * Filter form type for case edit match talent filtering popover.
 */
export type TCaseEditMatchFilterFormExtended = {
  status: string[]; // ステータス (multi-select)
  talentNumber: string; // 人材管理番号
  interviewDate: string[]; // 面接日 (multi-select from distinct dates)
  fullName: string; // 名前
  nickName: string; // 呼び名
  nationalityId: string[]; // 国籍
  gender: string[]; // 性別
  ageFrom: string; // 年齢（開始）
  ageTo: string; // 年齢（終了）
  residenceStatusId: string[]; // 在留資格
  periodOfStayFrom: string; // 在留期限（開始）
  periodOfStayTo: string; // 在留期限（終了）
  languageTestLevelId: string[]; // 日本語能力
  personInChargeId: string[]; // 担当者
  tagIds: string[]; // タグ (multi-select)
};

export type TCaseEditMatchDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const defaultCaseEditMatchFilterValues: TCaseEditMatchFilterFormExtended = {
  status: [],
  talentNumber: "",
  interviewDate: [],
  fullName: "",
  nickName: "",
  nationalityId: [],
  gender: [],
  ageFrom: "",
  ageTo: "",
  residenceStatusId: [],
  periodOfStayFrom: "",
  periodOfStayTo: "",
  languageTestLevelId: [],
  personInChargeId: [],
  tagIds: [],
};

export const defaultCaseEditMatchDisplayColumns: TCaseEditMatchDisplayColumnSetting[] = [
  { id: "status", label: "ステータス", visible: true, order: 1 },
  { id: "talentNumber", label: "人材管理番号", visible: true, order: 2 },
  { id: "interviewDate", label: "面接日", visible: true, order: 3 },
  { id: "fullName", label: "名前", visible: true, order: 4 },
  { id: "nickName", label: "呼び名", visible: true, order: 5 },
  { id: "tag", label: "タグ", visible: true, order: 6 },
  { id: "nationalityName", label: "国籍", visible: true, order: 7 },
  { id: "gender", label: "性別", visible: true, order: 8 },
  { id: "age", label: "年齢", visible: true, order: 9 },
  { id: "residenceStatusId", label: "在留資格", visible: true, order: 10 },
  { id: "periodOfStay", label: "在留期限", visible: true, order: 11 },
  { id: "languageCertificates", label: "日本語能力", visible: true, order: 12 },
  { id: "personInChargeId", label: "担当者", visible: true, order: 13 },
];

export const CASE_EDIT_MATCH_FILTER_FIELD_LABELS: { key: keyof TCaseEditMatchFilterFormExtended; label: string }[] = [
  { key: "status", label: "ステータス" },
  { key: "talentNumber", label: "人材管理番号" },
  { key: "interviewDate", label: "面接日" },
  { key: "fullName", label: "名前" },
  { key: "nickName", label: "呼び名" },
  { key: "nationalityId", label: "国籍" },
  { key: "gender", label: "性別" },
  { key: "ageFrom", label: "年齢" },
  { key: "residenceStatusId", label: "在留資格" },
  { key: "periodOfStayFrom", label: "在留期限" },
  { key: "languageTestLevelId", label: "日本語能力" },
  { key: "personInChargeId", label: "担当者" },
  { key: "tagIds", label: "タグ" },
];

/** Multi-select filter field keys */
export const CASE_EDIT_MATCH_MULTI_SELECT_FILTER_KEYS: (keyof TCaseEditMatchFilterFormExtended)[] = [
  "status",
  "interviewDate",
  "nationalityId",
  "gender",
  "residenceStatusId",
  "languageTestLevelId",
  "personInChargeId",
  "tagIds",
];

export const FILTER_LABEL_MAX_LENGTH = 30;

/** Locked columns that cannot be toggled off in display settings */
export const LOCKED_CASE_EDIT_MATCH_COLUMN_IDS = ["fullName"];

/** Date fields used for normalization */
export const CASE_EDIT_MATCH_DATE_FIELDS = ["periodOfStayFrom", "periodOfStayTo"] as const;

/** Check if a filter value is empty */
export const isEmptyCaseEditMatchFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyCaseEditMatchFilterFields = (filters: TCaseEditMatchFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyCaseEditMatchFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};

/** Normalize legacy scalar filter values to arrays for backward compatibility */
export const normalizeCaseEditMatchMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TCaseEditMatchFilterFormExtended> => {
  const result = { ...filters };
  for (const key of CASE_EDIT_MATCH_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TCaseEditMatchFilterFormExtended>;
};

export const reconcileCaseEditMatchDisplayColumns = (stored: TCaseEditMatchDisplayColumnSetting[]): TCaseEditMatchDisplayColumnSetting[] => {
  const defaultById = new Map(defaultCaseEditMatchDisplayColumns.map((c) => [c.id, c]));
  const defaultIndexById = new Map(defaultCaseEditMatchDisplayColumns.map((c, i) => [c.id, i]));

  const kept = [...stored]
    .filter((c) => defaultById.has(c.id))
    .sort((a, b) => a.order - b.order)
    .map((c) => ({ ...(defaultById.get(c.id) as TCaseEditMatchDisplayColumnSetting), visible: c.visible }));

  const storedIds = new Set(kept.map((c) => c.id));
  const result = [...kept];
  defaultCaseEditMatchDisplayColumns.forEach((col) => {
    if (storedIds.has(col.id)) return;
    const at = result.findIndex((c) => (defaultIndexById.get(c.id) ?? 0) > (defaultIndexById.get(col.id) ?? 0));
    result.splice(at === -1 ? result.length : at, 0, { ...col });
  });

  return result.map((c, i) => ({ ...c, order: i + 1 }));
};
